import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

interface Product {
  id: string;
  name: string;
  description?: string;
  category: string;
  brand?: string;
  model?: string;
  mainSupplierName?: string | null;
  costPrice: number;
  sellingPrice: number;
}

const normalizeText = (value?: string | null) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const normalizeCategoryKey = (value?: string | null) => {
  const raw = normalizeText(value).replace(/\s+/g, '_');
  if (!raw) return '';
  if (raw.includes('servic')) return 'servicio';
  if (raw.includes('neumatic') || raw.includes('cubierta') || raw.includes('goma')) return 'neumaticos';
  if (raw.includes('aceit')) return 'aceites';
  if (raw.includes('filtr')) return 'filtros';
  if (raw.includes('fren')) return 'frenos';
  if (raw.includes('motor')) return 'motor';
  if (raw.includes('transm')) return 'transmision';
  if (raw.includes('elect')) return 'electrico';
  if (raw.includes('carro')) return 'carroceria';
  if (raw.includes('herram')) return 'herramientas';
  if (raw.includes('consum')) return 'consumibles';
  if (raw.includes('otro')) return 'otros';
  return raw;
};

const detectMeasure = (name?: string | null) => {
  const text = String(name || '');
  const match =
    text.match(/\b\d{3}\/\d{2,3}R\d{2,3}[A-Z]?\b/i) ||
    text.match(/\b\d{2,3}R\d{2,3}(?:\.\d)?\b/i);
  return match ? match[0] : '';
};

const getCategoryLabel = (category: string) => {
  const key = normalizeCategoryKey(category);
  const categoryMap: Record<string, string> = {
    servicio: 'Servicio',
    neumaticos: 'Neumaticos',
    aceites: 'Aceites',
    filtros: 'Filtros',
    frenos: 'Frenos',
    motor: 'Motor',
    transmision: 'Transmision',
    electrico: 'Electrico',
    carroceria: 'Carroceria',
    herramientas: 'Herramientas',
    consumibles: 'Consumibles',
    otros: 'Otros',
  };
  if (categoryMap[key]) return categoryMap[key];
  return String(category || '')
    .replace(/_/g, ' ')
    .trim();
};

const formatMoney = (value?: number | null) => {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed) || parsed <= 0) return '-';
  return `$${parsed.toLocaleString('es-AR')}`;
};

const ProductMarkup: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [showMatches, setShowMatches] = useState(false);
  const [applying, setApplying] = useState(false);
  const [markupForm, setMarkupForm] = useState({
    itemType: 'all',
    category: 'all',
    markupPercent: '30',
    brand: '',
    model: '',
    measure: '',
    supplier: '',
    contains: '',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get('/api/products?limit=5000'),
        axios.get('/api/products/categories'),
      ]);
      const list = Array.isArray(productsRes.data?.products) ? productsRes.data.products : [];
      setProducts(
        list.map((p: any) => ({
          id: p.id,
          name: p.name || '',
          description: p.description || '',
          category: p.category || '',
          brand: p.brand || '',
          model: p.model || '',
          mainSupplierName: p.mainSupplierName || '',
          costPrice: Number(p.costPrice || 0),
          sellingPrice: Number(p.sellingPrice || p.salePrice || 0),
        }))
      );
      const serverCategories = Array.isArray(categoriesRes.data?.categories) ? categoriesRes.data.categories : [];
      setCategories(serverCategories.map((c: string) => normalizeCategoryKey(c)).filter(Boolean));
    } catch (error) {
      console.error('Error loading markup page', error);
      toast.error('No se pudieron cargar productos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selectedCategoryKey = normalizeCategoryKey(markupForm.category);
  const showBrandModelFilters = markupForm.itemType !== 'service' && selectedCategoryKey !== 'servicio';
  const showMeasureFilter = selectedCategoryKey === 'neumaticos';

  useEffect(() => {
    if (!showBrandModelFilters) {
      setMarkupForm((prev) => ({ ...prev, brand: '', model: '', measure: '' }));
      return;
    }
    if (!showMeasureFilter && markupForm.measure) {
      setMarkupForm((prev) => ({ ...prev, measure: '' }));
    }
  }, [showBrandModelFilters, showMeasureFilter, markupForm.measure]);

  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    categories.forEach((cat) => {
      if (cat) set.add(cat);
    });
    products.forEach((p) => {
      const key = normalizeCategoryKey(p.category);
      if (key) set.add(key);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [categories, products]);

  useEffect(() => {
    if (markupForm.itemType === 'service' && selectedCategoryKey !== 'all' && selectedCategoryKey !== 'servicio') {
      setMarkupForm((prev) => ({ ...prev, category: 'all' }));
    }
    if (markupForm.itemType === 'product' && selectedCategoryKey === 'servicio') {
      setMarkupForm((prev) => ({ ...prev, category: 'all' }));
    }
  }, [markupForm.itemType, selectedCategoryKey]);

  const matchesMarkupCriteria = useCallback((product: Product) => {
    const productCategory = normalizeCategoryKey(product.category);
    if (markupForm.itemType === 'product' && productCategory === 'servicio') return false;
    if (markupForm.itemType === 'service' && productCategory !== 'servicio') return false;
    if (markupForm.category !== 'all' && productCategory !== normalizeCategoryKey(markupForm.category)) return false;

    const normalizedName = normalizeText(product.name);
    const normalizedDescription = normalizeText(product.description);
    const normalizedBrand = normalizeText(product.brand);
    const normalizedModel = normalizeText(product.model);
    const normalizedSupplier = normalizeText(product.mainSupplierName);
    const normalizedMeasure = normalizeText(detectMeasure(product.name));

    const brandFilter = normalizeText(markupForm.brand);
    const modelFilter = normalizeText(markupForm.model);
    const measureFilter = normalizeText(markupForm.measure);
    const supplierFilter = normalizeText(markupForm.supplier);
    const containsFilter = normalizeText(markupForm.contains);

    if (brandFilter && !(normalizedBrand.includes(brandFilter) || normalizedName.includes(brandFilter))) return false;
    if (modelFilter && !(normalizedModel.includes(modelFilter) || normalizedName.includes(modelFilter))) return false;
    if (measureFilter && !(normalizedMeasure.includes(measureFilter) || normalizedName.includes(measureFilter))) return false;
    if (supplierFilter && !normalizedSupplier.includes(supplierFilter)) return false;
    if (containsFilter && !(normalizedName.includes(containsFilter) || normalizedDescription.includes(containsFilter))) return false;

    return true;
  }, [markupForm]);

  const matchedMarkupProducts = useMemo(
    () => products.filter(matchesMarkupCriteria),
    [products, matchesMarkupCriteria]
  );

  const handleApplyBulkMarkup = async () => {
    const targetMarkup = Number(markupForm.markupPercent);
    if (!Number.isFinite(targetMarkup) || targetMarkup < 0) {
      toast.error('Ingresa un markup valido (0 o mayor)');
      return;
    }

    const candidates = matchedMarkupProducts.filter(
      (p) => normalizeCategoryKey(p.category) !== 'servicio' && Number(p.costPrice || 0) > 0
    );

    if (!candidates.length) {
      toast.error('No hay productos con costo valido para aplicar markup');
      return;
    }

    setApplying(true);
    let updated = 0;
    let failed = 0;
    try {
      for (let i = 0; i < candidates.length; i += 8) {
        const chunk = candidates.slice(i, i + 8);
        const result = await Promise.all(
          chunk.map(async (product) => {
            try {
              const salePrice = Math.round(product.costPrice * (1 + targetMarkup / 100) * 100) / 100;
              await axios.put(`/api/products/${product.id}`, { salePrice });
              return true;
            } catch (error) {
              console.error('Error applying markup to', product.id, error);
              return false;
            }
          })
        );
        updated += result.filter(Boolean).length;
        failed += result.filter((ok) => !ok).length;
      }
      const skipped = matchedMarkupProducts.length - candidates.length;
      toast.success(`Markup aplicado. Actualizados: ${updated}. Omitidos: ${skipped}. Errores: ${failed}.`);
      await fetchData();
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Editar Markup</h2>
          <p className="text-sm text-muted-foreground">Actualiza markup por lote con filtros por tipo, categoría y atributos.</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/products')} className="gap-2">
          <ArrowLeftIcon className="h-4 w-4" />
          Volver
        </Button>
      </div>

      <div className="rounded-lg border p-4 space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Tipo</Label>
            <Select
              value={markupForm.itemType}
              onValueChange={(value) => setMarkupForm((prev) => ({ ...prev, itemType: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="product">Producto</SelectItem>
                <SelectItem value="service">Servicio</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{'Categor\u00eda'}</Label>
            <Select
              value={markupForm.category}
              onValueChange={(value) => setMarkupForm((prev) => ({ ...prev, category: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder={'Todas las categor\u00edas'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {categoryOptions.map((category) => (
                  <SelectItem key={category} value={category}>
                    {getCategoryLabel(category)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Markup (%) nuevo</Label>
            <Input
              type="number"
              min="0"
              step="0.1"
              value={markupForm.markupPercent}
              onChange={(e) => setMarkupForm((prev) => ({ ...prev, markupPercent: e.target.value }))}
            />
          </div>
          {showBrandModelFilters && (
            <>
              <div className="space-y-2">
                <Label>Marca (opcional)</Label>
                <Input
                  value={markupForm.brand}
                  onChange={(e) => setMarkupForm((prev) => ({ ...prev, brand: e.target.value }))}
                  placeholder="Ej: Wanli"
                />
              </div>
              <div className="space-y-2">
                <Label>Modelo (opcional)</Label>
                <Input
                  value={markupForm.model}
                  onChange={(e) => setMarkupForm((prev) => ({ ...prev, model: e.target.value }))}
                  placeholder="Ej: SP026"
                />
              </div>
            </>
          )}
          {showMeasureFilter && (
            <div className="space-y-2">
              <Label>Medida (opcional)</Label>
              <Input
                value={markupForm.measure}
                onChange={(e) => setMarkupForm((prev) => ({ ...prev, measure: e.target.value }))}
                placeholder="Ej: 175/65R14"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label>Proveedor (opcional)</Label>
            <Input
              value={markupForm.supplier}
              onChange={(e) => setMarkupForm((prev) => ({ ...prev, supplier: e.target.value }))}
              placeholder="Ej: San Juan y Solis"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Texto contiene (opcional)</Label>
            <Input
              value={markupForm.contains}
              onChange={(e) => setMarkupForm((prev) => ({ ...prev, contains: e.target.value }))}
              placeholder="Busca en nombre y descripcion"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
          <Button type="button" variant="secondary" onClick={() => setShowMatches((prev) => !prev)}>
            {matchedMarkupProducts.length} coinciden
          </Button>
          <Button onClick={handleApplyBulkMarkup} disabled={applying}>
            {applying ? 'Aplicando...' : 'Aplicar Markup'}
          </Button>
        </div>
      </div>

      {showMatches && (
        <div className="rounded-lg border">
          <div className="p-3 border-b text-sm text-muted-foreground">
            Productos filtrados para actualizar: <span className="font-medium text-foreground">{matchedMarkupProducts.length}</span>
          </div>
          <div className="max-h-[420px] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Producto</TableHead>
                  <TableHead>{'Categor\u00eda'}</TableHead>
                  <TableHead>Costo</TableHead>
                  <TableHead>Precio</TableHead>
                  <TableHead>Proveedor</TableHead>
                  <TableHead>Markup actual</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {matchedMarkupProducts.map((product) => {
                  const markup =
                    Number(product.costPrice || 0) > 0
                      ? ((Number(product.sellingPrice || 0) - Number(product.costPrice || 0)) / Number(product.costPrice || 0)) * 100
                      : null;
                  return (
                    <TableRow key={product.id}>
                      <TableCell>{product.name}</TableCell>
                      <TableCell>{getCategoryLabel(product.category)}</TableCell>
                      <TableCell>{formatMoney(product.costPrice)}</TableCell>
                      <TableCell>{formatMoney(product.sellingPrice)}</TableCell>
                      <TableCell>{product.mainSupplierName || '-'}</TableCell>
                      <TableCell>{markup === null ? '-' : `${markup.toFixed(1)}%`}</TableCell>
                    </TableRow>
                  );
                })}
                {!matchedMarkupProducts.length && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No hay coincidencias con los filtros actuales.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductMarkup;

